import type { ParseRoute } from "@tanstack/react-router"
import type { routeTree } from "routeTree.gen"
import { pluralize } from "utils/stringUtils"

import {
  GOODS_RECEIPT_PALLET_AS400_SYNC_PENDING_STATUSES,
  GOODS_RECEIPT_PALLET_ROW_LOT_EMPTY_FORM_VALUES,
  GOODS_RECEIPT_STATUS_TO_CHILD_ROUTE,
  GOODS_RECEIPT_STATUS_TO_DESKTOP_ROUTE,
  GOODS_RECEIPT_STATUS_TO_LABEL,
  GOODS_RECEIPT_STATUS_TO_TABLET_ROUTE,
} from "./constants"
import type {
  SoftReceivedLotFormValues,
  SoftReceivedPalletRowFormValues,
  SoftReceivedPurchaseOrderLineFormValues,
  SoftReceivedPurchaseOrdersFormValues,
} from "./schemas"
import type { GoodsReceiptTotals, PurchaseOrderLineQuantity, PurchaseOrderLineWithReceiptContext } from "./types"

import { UOM_RENDER_PRECISION } from "@/constants/formats"
import type {
  GoodsReceipt,
  GoodsReceiptPallet,
  GoodsReceiptPalletCreateInput,
  GoodsReceiptPalletRow,
  GoodsReceiptPalletRowLot,
  GoodsReceiptPalletRowLotCreateInput,
  GoodsReceiptProductDetail,
  GoodsReceiptStatus,
  Location,
  Order,
  OrderLine,
  Product,
} from "@/graphql/codegen/graphql"
import { WarehouseType } from "@/graphql/codegen/graphql"
import { toNumberOrNull, toStringOrEmpty } from "@/utils/forms"
import { roundToPrecision } from "@/utils/number"

export const getGoodsReceiptStatusLabel = (status: GoodsReceiptStatus): string => {
  return GOODS_RECEIPT_STATUS_TO_LABEL[status] ?? status
}

export const resolveGoodsReceiptPalletsCountFromPalletRows = (palletRows: GoodsReceiptPalletRow[]): number => {
  return roundToPrecision(
    palletRows.reduce((acc, palletRow) => acc + (palletRow.palletQty ?? 0), 0),
    2
  )
}

export const resolveGoodsReceiptPalletsCount = (goodsReceipt: GoodsReceipt): number | null => {
  const { estimatedPalletsCount, orders: purchaseOrders } = goodsReceipt

  if (purchaseOrders?.length) {
    return roundToPrecision(
      purchaseOrders
        .flatMap((order) => order.orderLines ?? [])
        .reduce((acc, purchaseOrderLine) => {
          const existingGoodsReceiptLine = goodsReceipt.productDetails?.find(
            (goodsReceiptLine) => goodsReceiptLine.orderLineId === purchaseOrderLine.orderLineId
          )
          const palletsCount = existingGoodsReceiptLine?.estimatedPallets ?? purchaseOrderLine.numberOfPallets ?? 0
          return acc + palletsCount
        }, 0),
      2
    )
  }

  return estimatedPalletsCount ?? null
}

export const formatPalletCount = (palletsCount: number | null): string =>
  palletsCount !== null ? `${palletsCount} ${pluralize(palletsCount, "Pallet", "Pallets")}` : "-"

export const resolveGoodsReceiptSupplierNames = (goodsReceipt: GoodsReceipt): string[] => {
  const { supplier, orders: purchaseOrders } = goodsReceipt

  if (purchaseOrders?.length) {
    return Array.from(
      new Set(
        purchaseOrders
          .flatMap(
            (purchaseOrder) =>
              purchaseOrder.orderLines?.map((purchaseOrderLine) => purchaseOrderLine.product?.company?.name) ?? []
          )
          .filter(Boolean) as string[]
      )
    )
  }

  return [supplier?.name ?? ""]
}

export const calculatePurchaseOrderEstimatedTotals = (purchaseOrderLines: OrderLine[]): GoodsReceiptTotals => {
  const { palletsCount, unitsCount, quantity, uomId } = purchaseOrderLines.reduce(
    (acc, purchaseOrderLine) => {
      const product = purchaseOrderLine.product as Product
      const palletsCount = purchaseOrderLine.numberOfPallets ?? 0
      const palletWeight = product.palletUsWeight ?? 0
      const packageWeight = product.packageUsWeight ?? 0
      const packageUom = product.packageUsWeightUom ?? ""

      const quantity = palletsCount * palletWeight
      const units = packageWeight > 0 ? quantity / packageWeight : 0

      acc.palletsCount += palletsCount
      acc.quantity += quantity
      acc.unitsCount += units
      acc.uomId = packageUom

      return acc
    },
    {
      palletsCount: 0,
      unitsCount: 0,
      quantity: 0,
      uomId: "",
    }
  )

  return {
    palletsCount: roundToPrecision(palletsCount, UOM_RENDER_PRECISION),
    unitsCount: roundToPrecision(unitsCount, UOM_RENDER_PRECISION),
    quantity: roundToPrecision(quantity, UOM_RENDER_PRECISION),
    uomId,
  }
}

export const calculatePurchaseOrderTotalsFromPalletRows = (
  purchaseOrderLines: OrderLine[],
  goodsReceiptLines: GoodsReceiptProductDetail[],
  goodsReceiptPalletRows: GoodsReceiptPalletRow[]
): GoodsReceiptTotals => {
  const uniqueUomIds = new Set<string>()

  const totals = purchaseOrderLines.reduce(
    (acc, purchaseOrderLine) => {
      const product = purchaseOrderLine.product!

      uniqueUomIds.add(product.packageUsWeightUom!)

      const existingGoodsReceiptLine = goodsReceiptLines?.find(
        (goodsReceiptLine) => goodsReceiptLine.orderLineId === purchaseOrderLine.orderLineId
      )

      if (!existingGoodsReceiptLine) {
        return acc
      }

      const purchaseOrderLinePalletRows = goodsReceiptPalletRows.filter(
        (palletRow) => palletRow.goodsReceiptProductDetailId === existingGoodsReceiptLine.goodsReceiptProductDetailId
      )

      const palletRowTotals = calculateGoodsReceiptPalletRowTotals(purchaseOrderLinePalletRows)

      return {
        palletsCount: acc.palletsCount + palletRowTotals.palletsCount,
        unitsCount: acc.unitsCount + palletRowTotals.unitsCount,
        quantity: acc.quantity + palletRowTotals.quantity,
        uomId: product.packageUsWeightUom!,
      }
    },
    { palletsCount: 0, unitsCount: 0, quantity: 0, uomId: "" }
  )

  const hasSameUom = uniqueUomIds.size <= 1

  return {
    palletsCount: roundToPrecision(totals.palletsCount, 2),
    unitsCount: roundToPrecision(totals.unitsCount, 2),
    quantity: hasSameUom ? roundToPrecision(totals.quantity, 2) : null,
    uomId: hasSameUom ? totals.uomId : "",
  }
}

export const calculateGoodsReceiptPalletRowTotals = (
  palletRows: GoodsReceiptPalletRow[]
): { palletsCount: number; unitsCount: number; quantity: number } => {
  const totals = palletRows.reduce(
    (acc, palletRow) => {
      const palletsCount = palletRow.palletQty ?? 0
      const lotTotals = (palletRow.lots ?? []).reduce(
        (lotAcc, lot) => ({
          unitsCount: lotAcc.unitsCount + (lot.units ?? 0) * palletsCount,
          quantity: lotAcc.quantity + (lot.totalUom ?? 0) * palletsCount,
        }),
        { unitsCount: 0, quantity: 0 }
      )

      return {
        palletsCount: acc.palletsCount + palletsCount,
        unitsCount: acc.unitsCount + lotTotals.unitsCount,
        quantity: acc.quantity + lotTotals.quantity,
      }
    },
    { palletsCount: 0, unitsCount: 0, quantity: 0 }
  )

  return {
    palletsCount: roundToPrecision(totals.palletsCount, 2),
    unitsCount: roundToPrecision(totals.unitsCount, 2),
    quantity: roundToPrecision(totals.quantity, 2),
  }
}

export const getPurchaseOrderContainerTrackingNumbers = (purchaseOrder: Order): string[] => {
  const { containerTrackingNumber, shippingLegs } = purchaseOrder
  const containerTrackingNumbers = [
    containerTrackingNumber ?? "",
    ...(shippingLegs?.map((shippingLeg) => shippingLeg.containerTrackingNumber ?? "") ?? []),
  ].filter(Boolean)

  return Array.from(new Set(containerTrackingNumbers))
}

export const getGoodsReceiptContainerTrackingNumbers = (goodsReceipt: GoodsReceipt): string[] => {
  const containerTrackingNumbers = [
    ...(goodsReceipt.trackingNumbers ?? []),
    ...(goodsReceipt.orders?.flatMap((purchaseOrder) => getPurchaseOrderContainerTrackingNumbers(purchaseOrder)) ?? []),
  ]

  return Array.from(new Set(containerTrackingNumbers))
}

export const getGoodsReceiptPurchaseOrderLines = (
  goodsReceipt: GoodsReceipt
): PurchaseOrderLineWithReceiptContext[] => {
  const purchaseOrders = goodsReceipt.orders ?? []

  return purchaseOrders.flatMap((purchaseOrder) =>
    (purchaseOrder.orderLines ?? []).map((orderLine) => {
      const goodsReceiptLine = goodsReceipt.productDetails?.find(
        (productDetail) => productDetail.orderLineId === orderLine.orderLineId
      )

      const palletRows = (goodsReceipt.palletRows ?? []).filter(
        (palletRow) => palletRow.goodsReceiptProductDetailId === goodsReceiptLine?.goodsReceiptProductDetailId
      )

      const costBreakdown =
        goodsReceiptLine &&
        goodsReceipt.costBreakdown?.lineCostBreakdowns?.find(
          (breakdown) => breakdown.goodsReceiptProductDetailId === goodsReceiptLine.goodsReceiptProductDetailId
        )

      return {
        ...orderLine,
        containerTrackingNumbers: getPurchaseOrderContainerTrackingNumbers(purchaseOrder),
        goodsReceiptLine: goodsReceiptLine ?? null,
        palletRows,
        costBreakdown: costBreakdown ?? null,
      }
    })
  )
}

export const resolveGoodsReceiptPurchaseOrderLineQuantity = (
  purchaseOrderLine: PurchaseOrderLineWithReceiptContext
): PurchaseOrderLineQuantity => {
  const product = purchaseOrderLine.product as Product
  const palletUsWeight = product.palletUsWeight ?? 0
  const palletMetricWeight = product.palletMetricWeight ?? 0
  const usUomId = product.packageUsWeightUom ?? ""
  const metricUomId = product.packageMetricWeightUom ?? ""

  const palletsCount = purchaseOrderLine.goodsReceiptLine?.estimatedPallets ?? purchaseOrderLine.numberOfPallets ?? 0

  return {
    palletsCount,
    regional: {
      us: {
        quantity: roundToPrecision(palletsCount * palletUsWeight, 2),
        uomId: usUomId,
      },
      metric: {
        quantity: roundToPrecision(palletsCount * palletMetricWeight, 2),
        uomId: metricUomId,
      },
    },
  }
}

export const hasGoodsReceiptLineValues = ({
  estimatedPallets,
  estimatedUnits,
  estimatedTotalUom,
}: GoodsReceiptProductDetail): boolean =>
  // eslint-disable-next-line eqeqeq
  [estimatedPallets, estimatedUnits, estimatedTotalUom].every((value) => value != null)

export const calculateGoodsReceiptPalletQuantity = (lots: GoodsReceiptPalletRowLot[]): number =>
  lots.reduce((acc, lot) => acc + (lot.totalUom ?? 0), 0)

export const formatDestinationLabel = (destination: {
  aisleCode: string
  sectionCode: string
  binNumber: number
}): string => {
  return `${destination.aisleCode}–${destination.sectionCode}–${destination.binNumber}`
}

export const formatPutAwaySuccessMessage = (
  palletCount: number,
  destination?: { aisleCode: string; sectionCode: string; binNumber: number } | null
): string => {
  if (palletCount === 1 && destination) {
    return `Pallet successfully stored at ${formatDestinationLabel(destination)}.`
  }
  return `${palletCount} ${pluralize(palletCount, "pallet", "pallets")} successfully stored.`
}

export const isAkrochemManagedWarehouse = (warehouseType?: WarehouseType | null): boolean => {
  return warehouseType === WarehouseType.AkrochemManaged
}

export const getOrderShipToLocation = (order: Order): Location | null => {
  return (
    order.destinationLocationAssociationDetail?.companyLocationAssociation?.shippingLocationDetail?.location ?? null
  )
}

export interface OrderWarehouseInfo {
  location: Location | null
  warehouse: Order["warehouse"] | null
}

export const getOrderWarehouseInfo = (order: Order): OrderWarehouseInfo => {
  const location = getOrderShipToLocation(order)
  const warehouse = order.warehouse ?? null

  return {
    location,
    warehouse,
  }
}

export const getGoodsReceiptDeliveryLocation = (goodsReceipt: GoodsReceipt | null | undefined): Location | null => {
  return goodsReceipt?.warehouseArea?.location ?? null
}

export const hasCompatiblePurchaseOrderShipToLocations = (order1: Order, order2: Order): boolean => {
  const warehouseInfo1 = getOrderWarehouseInfo(order1)
  const warehouseInfo2 = getOrderWarehouseInfo(order2)

  const hasWarehouse1 = !!warehouseInfo1.warehouse?.warehouseAreaId
  const hasWarehouse2 = !!warehouseInfo2.warehouse?.warehouseAreaId

  // If both orders have warehouse information, compare warehouseAreaId
  if (hasWarehouse1 && hasWarehouse2) {
    return warehouseInfo1.warehouse?.warehouseAreaId === warehouseInfo2.warehouse?.warehouseAreaId
  }

  // If one has warehouse and the other doesn't, they are incompatible
  if (hasWarehouse1 !== hasWarehouse2) {
    return false
  }

  // For legacy POs where both don't have warehouseAreaId, fall back to location comparison
  return warehouseInfo1.location?.locationId === warehouseInfo2.location?.locationId
}

export const isPurchaseOrderSelectable = (
  orderToAdd: Order,
  selectedOrders: Order[],
  goodsReceiptDeliveryLocation?: Location | null
): boolean => {
  if (!orderToAdd.as400OrderNumber) return false

  // Validate compatibility with existing goods receipt delivery location
  if (goodsReceiptDeliveryLocation) {
    const orderWarehouseInfo = getOrderWarehouseInfo(orderToAdd)

    if (!orderWarehouseInfo.location) return false
    if (orderWarehouseInfo.location.locationId !== goodsReceiptDeliveryLocation.locationId) return false
  }

  // If no other orders selected, this order can be added
  if (selectedOrders.length === 0) return true

  // Validate compatibility with all currently selected orders (includes warehouse validation)
  return selectedOrders.every((selectedOrder) => hasCompatiblePurchaseOrderShipToLocations(orderToAdd, selectedOrder))
}

export const getGoodsReceiptLotsFormValues = (
  lots: GoodsReceiptPalletRowLot[],
  product: Product
): SoftReceivedLotFormValues[] => {
  return lots.map((lot) => ({
    goodsReceiptPalletRowLotId: lot.goodsReceiptPalletRowLotId,
    lotNumber: toStringOrEmpty(lot.lotNumber),
    batchNumber: toStringOrEmpty(lot.batchNumber),
    packageUnitQuantity: toNumberOrNull(lot.packageUnitQuantity) ?? product.packageUsWeight ?? null,
    packagingType: lot.packagingType ?? product.packagingType ?? null,
    unitsCount: lot.units ?? null,
    quantity: lot.totalUom ?? null,
  })) as SoftReceivedLotFormValues[]
}

export const getGoodsReceiptRoutePathFactory = (
  status: GoodsReceiptStatus,
  isTabletView: boolean
): ((goodsReceipt: GoodsReceipt) => ParseRoute<typeof routeTree>["fullPath"]) => {
  const route = isTabletView
    ? GOODS_RECEIPT_STATUS_TO_TABLET_ROUTE.get(status)
    : GOODS_RECEIPT_STATUS_TO_DESKTOP_ROUTE.get(status)
  const suffix = GOODS_RECEIPT_STATUS_TO_CHILD_ROUTE[status] ?? ""

  return (goodsReceipt: GoodsReceipt) => `${route}/${goodsReceipt.goodsReceiptId}${suffix}`
}

export const getSoftReceivedPalletRowFormEmptyValues = (product: Product): SoftReceivedPalletRowFormValues => ({
  goodsReceiptPalletRowId: null,
  palletsCount: null,
  palletUnitQuantity: product.palletUsWeight ?? null,
  lots: [
    {
      ...GOODS_RECEIPT_PALLET_ROW_LOT_EMPTY_FORM_VALUES,
      packageUnitQuantity: product.packageUsWeight ?? null,
      packagingType: product.packagingType ?? null,
    },
  ],
})

const getPurchaseOrderLineFormDefaultValues = (
  purchaseOrderLine: OrderLine,
  goodsReceiptLines: GoodsReceiptProductDetail[],
  goodsReceiptPalletRows: GoodsReceiptPalletRow[],
  shouldPrepopulateEmptyPalletRow: boolean
): SoftReceivedPurchaseOrderLineFormValues => {
  const product = purchaseOrderLine.product!
  const existingGoodsReceiptLine = goodsReceiptLines.find(
    (goodsReceiptLine) => goodsReceiptLine.orderLineId === purchaseOrderLine.orderLineId
  )
  const existingPalletRows = goodsReceiptPalletRows.filter(
    (palletRow) => palletRow.goodsReceiptProductDetailId === existingGoodsReceiptLine?.goodsReceiptProductDetailId
  )

  let palletRows: SoftReceivedPalletRowFormValues[]

  if (existingPalletRows.length > 0) {
    palletRows = existingPalletRows.map((palletRow) => {
      return {
        goodsReceiptPalletRowId: palletRow.goodsReceiptPalletRowId,
        palletsCount: palletRow.palletQty ?? null,
        palletUnitQuantity: toNumberOrNull(palletRow.palletUnitQuantity) ?? product.palletUsWeight ?? null,
        lots: getGoodsReceiptLotsFormValues(palletRow.lots ?? [], product),
      }
    })
  } else {
    palletRows = shouldPrepopulateEmptyPalletRow ? [getSoftReceivedPalletRowFormEmptyValues(product)] : []
  }

  return {
    purchaseOrderLineId: purchaseOrderLine.orderLineId,
    uomId: product.packageUsWeightUom!,
    goodsReceiptLineId: existingGoodsReceiptLine?.goodsReceiptProductDetailId ?? "",
    palletRows,
  }
}

export const getSoftReceivedPurchaseOrdersFormDefaultValues = (
  goodsReceipt: GoodsReceipt,
  goodsReceiptPalletRows: GoodsReceiptPalletRow[],
  shouldPrepopulateEmptyPalletRow = true
): SoftReceivedPurchaseOrdersFormValues => {
  const goodsReceiptLines = goodsReceipt.productDetails ?? []

  return {
    goodsReceiptId: goodsReceipt.goodsReceiptId,
    purchaseOrders: (goodsReceipt.orders ?? []).map((order) => ({
      purchaseOrderId: order.orderId,
      lines: (order.orderLines ?? []).map((purchaseOrderLine) =>
        getPurchaseOrderLineFormDefaultValues(
          purchaseOrderLine,
          goodsReceiptLines,
          goodsReceiptPalletRows,
          shouldPrepopulateEmptyPalletRow
        )
      ),
    })),
  }
}

export const resolveSoftReceivedPurchaseOrderEstimatedTotals = (
  purchaseOrderLines: OrderLine[],
  goodsReceiptLines: GoodsReceiptProductDetail[]
): GoodsReceiptTotals => {
  const uniqueUomIds = new Set<string>()

  const totals = purchaseOrderLines.reduce(
    (acc, purchaseOrderLine) => {
      const product = purchaseOrderLine.product!

      uniqueUomIds.add(product.packageUsWeightUom!)

      const existingGoodsReceiptLine = goodsReceiptLines?.find(
        (goodsReceiptLine) => goodsReceiptLine.orderLineId === purchaseOrderLine.orderLineId
      )

      if (existingGoodsReceiptLine && hasGoodsReceiptLineValues(existingGoodsReceiptLine)) {
        return {
          palletsCount: acc.palletsCount + (existingGoodsReceiptLine.estimatedPallets ?? 0),
          unitsCount: acc.unitsCount + (existingGoodsReceiptLine.estimatedUnits ?? 0),
          quantity: acc.quantity + (existingGoodsReceiptLine.estimatedTotalUom ?? 0),
          uomId: product.packageUsWeightUom!,
        }
      } else {
        const purchaseOrderTotals = calculatePurchaseOrderEstimatedTotals([purchaseOrderLine])

        return {
          palletsCount: acc.palletsCount + purchaseOrderTotals.palletsCount,
          unitsCount: acc.unitsCount + purchaseOrderTotals.unitsCount,
          quantity: acc.quantity + (purchaseOrderTotals.quantity ?? 0),
          uomId: purchaseOrderTotals.uomId,
        }
      }
    },
    { palletsCount: 0, unitsCount: 0, quantity: 0, uomId: "" }
  )

  const hasSameUom = uniqueUomIds.size <= 1

  return {
    palletsCount: roundToPrecision(totals.palletsCount, 2),
    unitsCount: roundToPrecision(totals.unitsCount, 2),
    quantity: hasSameUom ? roundToPrecision(totals.quantity, 2) : null,
    uomId: hasSameUom ? totals.uomId : "",
  }
}

export const calculateSoftReceivedPurchaseOrderFormTotals = (
  purchaseOrderLines: SoftReceivedPurchaseOrderLineFormValues[]
): GoodsReceiptTotals => {
  const uniqueUomIds = new Set<string>()

  const totals = purchaseOrderLines.reduce(
    (acc, line) => {
      uniqueUomIds.add(line.uomId)

      const lineTotals = line.palletRows.reduce(
        (palletAcc, palletRow) => {
          const palletsCount = palletRow.palletsCount ?? 0
          const lotTotals = palletRow.lots.reduce(
            (lotAcc, lot) => ({
              unitsCount: (lotAcc.unitsCount ?? 0) + (lot.unitsCount ?? 0) * palletsCount,
              quantity: (lotAcc.quantity ?? 0) + (lot.quantity ?? 0) * palletsCount,
            }),
            { unitsCount: 0 as number | null, quantity: 0 as number | null }
          )

          return {
            palletsCount: palletAcc.palletsCount + palletsCount,
            unitsCount: palletAcc.unitsCount + (lotTotals.unitsCount ?? 0),
            quantity: palletAcc.quantity + (lotTotals.quantity ?? 0),
          }
        },
        { palletsCount: 0, unitsCount: 0, quantity: 0 }
      )

      return {
        palletsCount: acc.palletsCount + lineTotals.palletsCount,
        unitsCount: acc.unitsCount + lineTotals.unitsCount,
        quantity: acc.quantity + lineTotals.quantity,
        uomId: line.uomId,
      }
    },
    { palletsCount: 0, unitsCount: 0, quantity: 0, uomId: "" }
  )

  const hasSameUom = uniqueUomIds.size <= 1

  return {
    palletsCount: roundToPrecision(totals.palletsCount, 2),
    unitsCount: roundToPrecision(totals.unitsCount, 2),
    quantity: hasSameUom ? roundToPrecision(totals.quantity, 2) : null,
    uomId: hasSameUom ? totals.uomId : "",
  }
}

const mapLotToCreateInput = (lot: SoftReceivedLotFormValues): GoodsReceiptPalletRowLotCreateInput => {
  return {
    lotNumber: lot.lotNumber,
    batchNumber: lot.batchNumber || "",
    units: lot.unitsCount,
    totalUom: lot.quantity,
    packageUnitQuantity: lot.packageUnitQuantity?.toString() ?? null,
    packagingType: lot.packagingType,
  }
}

const mapPalletRowToPalletCreateInput = (
  goodsReceiptLineId: string,
  palletRow: SoftReceivedPalletRowFormValues
): GoodsReceiptPalletCreateInput[] => {
  const palletsCount = palletRow.palletsCount || 0

  if (palletsCount === 0) {
    return []
  }

  return Array.from({ length: palletsCount }, () => ({
    goodsReceiptProductDetailId: goodsReceiptLineId,
    palletUnitQuantity: palletRow.palletUnitQuantity?.toString() ?? null,
    lots: palletRow.lots.map(mapLotToCreateInput),
  }))
}

export const serializeGoodsReceiptPalletsCreateForm = (
  formValues: SoftReceivedPurchaseOrdersFormValues
): GoodsReceiptPalletCreateInput[] => {
  return formValues.purchaseOrders
    .flatMap((purchaseOrder) => purchaseOrder.lines)
    .flatMap((purchaseOrderLine) => purchaseOrderLine.palletRows.map((palletRow) => ({ purchaseOrderLine, palletRow })))
    .flatMap(({ purchaseOrderLine, palletRow }) =>
      mapPalletRowToPalletCreateInput(purchaseOrderLine.goodsReceiptLineId, palletRow)
    )
}

export const isPalletAs400SyncPending = (pallet: GoodsReceiptPallet): boolean =>
  !!pallet.as400SyncStatus && GOODS_RECEIPT_PALLET_AS400_SYNC_PENDING_STATUSES.includes(pallet.as400SyncStatus)
